import {
  Snackbar,
  Alert,
  Grid,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PersonAddAlt } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import AddUserModal from "../usuarios/components/addUserModal";
import { useState, useEffect } from "react";
import userService from "../../services/userService";

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [alertObj, setAlertObj] = useState({
    severity: "success",
    message: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers();
      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const handleOpenModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const addUser = async (newUser, isEditing) => {
    try {
      if (isEditing) {
        await userService.updateUser({ ...newUser, id: editingUser?.id });
        setAlertObj({ severity: "success", message: "Colaborador actualizado" });
      } else {
        await userService.createUser(newUser);
        setAlertObj({ severity: "success", message: "Colaborador creado exitosamente" });
      }
      setOpenSnack(true);
      fetchUsers();
    } catch (error) {
      console.error(isEditing ? "Error updating user:" : "Error creating user:", error);
      setAlertObj({
        severity: "error",
        message: isEditing
          ? "No se pudo actualizar el colaborador"
          : "Ocurrió un error al crear el colaborador",
      });
      setOpenSnack(true);
      throw error; // el modal muestra su propio error y no se cierra
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmUser) return;
    setDeleting(true);
    try {
      await userService.deactivateUser(confirmUser.id);
      setAlertObj({ severity: "success", message: "Colaborador eliminado" });
      setOpenSnack(true);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setAlertObj({ severity: "error", message: "No se pudo eliminar el colaborador" });
      setOpenSnack(true);
    } finally {
      setDeleting(false);
      setConfirmUser(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search)
    );
  });

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  const EmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
      }}
    >
      <PersonAddAlt
        sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }}
      />
      <Typography variant="body1" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
        No hay usuarios registrados
      </Typography>
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ mb: 2, textAlign: "center" }}
      >
        Agrega usuarios para que puedan acceder al sistema
      </Typography>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpenModal}
        sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
      >
        Agregar primer usuario
      </Button>
    </Box>
  );

  return (
    <Box sx={{ px: 5, py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            pl: 1.5,
            borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
            lineHeight: 1.5,
          }}
        >
          Colaboradores
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            px: 3,
          }}
        >
          Agregar colaborador
        </Button>
      </Box>

      {/* Search bar */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          mb: 2,
          borderRadius: "10px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        <TextField
          placeholder="Buscar por nombre, email o rol..."
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 320,
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          }}
          autoComplete="off"
        />
        {users.length > 0 && (
          <Chip
            label={`${users.length} colaborador${users.length !== 1 ? "es" : ""}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        )}
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    width: "30%",
                  }}
                >
                  Nombre
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    width: "30%",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    width: "15%",
                  }}
                >
                  Rol
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    width: "20%",
                  }}
                >
                  Estado
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    width: "15%",
                  }}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <TableRow
                    key={user.id}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "grey.50" },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            bgcolor: "primary.main",
                          }}
                        >
                          {getInitial(user.name)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || "Sin rol"}
                        size="small"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          bgcolor: "grey.100",
                          color: "text.primary",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.email_verified ? "Verificado" : "Falta verificar"}
                        size="small"
                        color={user.email_verified ? "success" : "warning"}
                        variant={user.email_verified ? "filled" : "outlined"}
                        sx={{ fontSize: "0.72rem", fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={user.is_owner ? "No se puede editar al propietario" : "Editar"} arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={user.is_owner}
                            onClick={() => handleEditUser(user)}
                            sx={{
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "#E8F0FE" },
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={user.is_owner ? "No se puede eliminar al propietario" : "Eliminar"} arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={user.is_owner}
                            onClick={() => setConfirmUser(user)}
                            sx={{
                              borderRadius: "8px",
                              "&:hover": { bgcolor: "error.50", color: "error.main" },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ padding: 0, border: "none" }}>
                    <EmptyState />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AddUserModal
        open={modalOpen}
        handleClose={handleCloseModal}
        addUser={addUser}
        editingUser={editingUser}
      />

      <Dialog open={Boolean(confirmUser)} onClose={() => !deleting && setConfirmUser(null)}>
        <DialogTitle>¿Eliminar a este colaborador?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmUser?.name || confirmUser?.email} dejará de tener acceso al sistema. Podés volver a activarlo más adelante.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUser(null)} disabled={deleting} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={deleting} sx={{ textTransform: "none" }}>
            {deleting ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnack}
        autoHideDuration={3000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnack}
          severity={alertObj?.severity}
          sx={{ width: "100%" }}
        >
          {alertObj.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
